import { CreatorRankingPageProps } from './CreatorRankingPage';
import { CreatorRankingTopTen } from '../../../components/cells/CreatorRankingTopTen/CreatorRankingTopTen';
import { CreatorRankingItem } from '../../../components/cells/CreatorRankingItem/CreatorRankingItem';
import { map, pipe, toArray, zipWithIndex } from '@fxts/core';
import { CoreUiS } from '../../../../features/Core/outbound/share';
import { SectionTabs } from '../../../../shared/components/atoms/SectionTabs/SectionTabs';
import { SectionTitle } from '../../../components/cells/SectionTitle/SectionTitle';
import { CalendarPlugin } from '../../../components/cells/SectionTitle/plugins';
import { ListView, View } from 'rune-ts';
import { CalendarSelectView } from '../../../components/cells/CalendarView/CalendarView';
import { ProductCard, ProductLikeToggleEvent } from '../../../components/cells/ProductCard/ProductCard';
import { CreatorApiS } from '../../../../features/Creator/outbound/share';
import { ConfigSentryF } from '../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { Alert } from '../../../../shared/components/cells/Alert/Alert';
import { preventEscapeErrorJson } from '../../../../shared/util/preventEscapeErrorJson';
import { MarppleShopLoadingView } from '../../../components/atoms/Loading/MarppleShopLoadingView';
import { ICreatorRanking } from '@services/MarppleShop/features/Creator/type';
import { isThisMonth, isThisWeek } from 'date-fns';

type CreatorRankingTabs = {
  [key in CreatorRankingPageProps['current_tab_id']]: key;
};

class CreatorRankingList extends ListView<CreatorRankingItem> {
  override tagName = 'ul';
  ItemView = CreatorRankingItem;
}

export class CreatorRankingPageController {
  public Loading = new MarppleShopLoadingView();

  public readonly texts: ReturnType<typeof this.makeTexts>;

  public titleView: SectionTitle;

  public tabsView: SectionTabs;

  public sectionTitleView: SectionTitle;

  public creatorRankingTopTenView: CreatorRankingTopTen;

  public creatorRankingListView: CreatorRankingList;

  constructor(data: CreatorRankingPageProps) {
    this.texts = this.makeTexts(data.current_tab_id);
    const { texts } = this;

    this.titleView = CoreUiS.makeCreatorRankingTitle(data.layout_options.is_mobile, 'creator_ranking');

    this.tabsView = new SectionTabs({
      tabs: [
        {
          id: CreatorRankingPageController.TABS.realtime,
          label: texts.realtime,
          href: `/${ET.lang}/@/creator/ranking/${CreatorRankingPageController.TABS.realtime}`,
        },
        {
          id: CreatorRankingPageController.TABS.weekly,
          label: texts.weekly,
          href: `/${ET.lang}/@/creator/ranking/${CreatorRankingPageController.TABS.weekly}`,
        },
        {
          id: CreatorRankingPageController.TABS.monthly,
          label: texts.monthly,
          href: `/${ET.lang}/@/creator/ranking/${CreatorRankingPageController.TABS.monthly}`,
        },
      ],
      current_tab_id: data.current_tab_id,
      size: data.layout_options.is_mobile ? 'xSmall' : 'small',
    });

    this.sectionTitleView = new SectionTitle({
      plugins:
        data.current_tab_id === 'realtime'
          ? []
          : [
              new CalendarPlugin({
                tab: data.current_tab_id,
                is_mobile: data.layout_options.is_mobile,
                hide_period: true,
              }),
            ],
      title_props: {
        title: this.tabsView,
        size: data.layout_options.is_mobile ? 'xSmall' : 'small',
      },
    });

    this.creatorRankingTopTenView = new CreatorRankingTopTen({
      creator_rankings: data.creator_rankings,
      ranking_page_type: data.current_tab_id,
      updated_at: data.updated_at,
      is_mobile: data.layout_options.is_mobile,
    });

    this.creatorRankingListView = new CreatorRankingList(
      this.makeCreatorRankingItemDataList(data, () => false),
    );
  }

  static TABS: CreatorRankingTabs = {
    realtime: 'realtime',
    weekly: 'weekly',
    monthly: 'monthly',
  };

  private makeCreatorRankingItemDataList(
    data: CreatorRankingPageProps,
    activateFn: (index: number, creator_ranking: ICreatorRanking) => boolean,
  ) {
    return pipe(
      data.creator_rankings,
      zipWithIndex,
      map(([index, creator_ranking]) => ({
        creator: creator_ranking,
        active: activateFn(index, creator_ranking),
        default_product_card_props: {
          user_id: data.layout_options.user?.id,
        },
        is_mobile: data.layout_options.is_mobile,
      })),
      toArray,
    );
  }

  public makeTexts(current_tab_id: CreatorRankingPageProps['current_tab_id']) {
    const tab_name = ET(`mps2::creator_ranking::${current_tab_id}`);

    return {
      title: ET('mps2::creator_ranking::title', { tab: tab_name }),
      title_mo: ET('mps2::creator_ranking::title_mo', { tab: tab_name }),
      sub_title: ET('mps2::creator_ranking::sub_title'),
      realtime: ET('mps2::creator_ranking::realtime'),
      weekly: ET('mps2::creator_ranking::weekly'),
      monthly: ET('mps2::creator_ranking::monthly'),
      list_title: ET('mps2::creator_ranking::list_title'),
      home: ET('mps2::breadcrumbs::home'),
      creator_ranking: ET('mps2::breadcrumbs::creator_ranking'),
    };
  }

  public makeSectionTitle(is_mobile: boolean) {
    return is_mobile ? `${this.texts.title_mo} Top 100` : `${this.texts.title} Top 100`;
  }

  public delegateProductLikeEvent(view: View) {
    view.delegate(ProductLikeToggleEvent, ProductCard, async (_, cardView) => {
      await cardView.toggleLikeButton();
    });
  }

  public delegateCalendarChangeEvent(data: CreatorRankingPageProps, view: View<CreatorRankingPageProps>) {
    view.addEventListener(CalendarSelectView.ChangeDateEvent, async (e) => {
      try {
        const { date } = e.detail;

        this.Loading.show();
        const { creator_rankings, updated_at } = await CreatorApiS.getCreatorRankings(
          data.current_tab_id,
          date,
        );

        view.data.creator_rankings = creator_rankings;
        if (data.current_tab_id === 'weekly' && !isThisWeek(date)) {
          view.data.updated_at = date;
        } else if (data.current_tab_id === 'monthly' && !isThisMonth(date)) {
          view.data.updated_at = date;
        } else {
          view.data.updated_at = updated_at;
        }

        this.creatorRankingListView.set(
          this.makeCreatorRankingItemDataList(view.data, (index) => index === 0),
        );
        this.creatorRankingListView.redraw();

        this.creatorRankingTopTenView.setDataAndRedraw({
          creator_rankings,
          updated_at: view.data.updated_at,
        });
      } catch (e) {
        ConfigSentryF.error(e);
        return Alert.open(
          preventEscapeErrorJson({
            title: ET('mps2::common::error_title'),
            message: ET('mps2::common::error_message'),
          }),
        );
      } finally {
        this.Loading.hide();
      }
    });
  }
}
